"use client"

import {
    Avatar,
    AvatarFallback,
    AvatarImage
} from "@papier/ui/components/ui/avatar"
import { Button } from "@papier/ui/components/ui/button"
import { Card, CardContent } from "@papier/ui/components/ui/card"
import { cn } from "@papier/ui/lib/utils"
import { Buildings as Building, Check, Close as X } from "@papier/ui/components/icons"
import { useCallback, useContext, useMemo, useState } from "react"
import {
    AuthUIContext,
    EMAIL_VERIFICATION_REQUIRED_FOR_INVITATION
} from "../../lib/auth-ui-provider"
import { getLocalizedError } from "../../lib/utils"
import type { SettingsCardProps } from "../settings/shared/settings-card"
import { SettingsCard } from "../settings/shared/settings-card"

export function UserInvitationsCard({
    className,
    classNames,
    localization: localizationProp,
    onInvalidateInvitations,
    onAcceptInvitation,
    ...props
}: SettingsCardProps & {
    onInvalidateInvitations?: () => Promise<void>
    onAcceptInvitation?: (invitationId: string) => Promise<void> | void
}) {
    const {
        authClient,
        toast,
        hooks: { useListUserInvitations, useListOrganizations },
        localization: contextLocalization
    } = useContext(AuthUIContext)

    const [isLoading, setIsLoading] = useState(false)

    const localization = useMemo(
        () => ({ ...contextLocalization, ...localizationProp }),
        [contextLocalization, localizationProp]
    )

    const {
        data: invitations,
        error: invitationsError,
        refetch: refetchInvitations
    } = useListUserInvitations()
    const { refetch: refetchOrganizations } = useListOrganizations()

    const handleAccept = useCallback(
        async (invitationId: string) => {
            setIsLoading(true)

            try {
                await authClient.organization.acceptInvitation({
                    invitationId,
                    fetchOptions: { throw: true }
                })

                await Promise.all([
                    refetchInvitations?.(),
                    refetchOrganizations?.(),
                    onInvalidateInvitations?.()
                ])

                toast({
                    variant: "success",
                    message: localization.INVITATION_ACCEPTED
                })

                await onAcceptInvitation?.(invitationId)
            } catch (error) {
                toast({
                    variant: "error",
                    message: getLocalizedError({ error, localization })
                })
            }

            setIsLoading(false)
        },
        [
            authClient,
            toast,
            localization,
            refetchInvitations,
            refetchOrganizations,
            onInvalidateInvitations,
            onAcceptInvitation
        ]
    )

    const handleReject = useCallback(
        async (invitationId: string) => {
            setIsLoading(true)

            try {
                await authClient.organization.rejectInvitation({
                    invitationId,
                    fetchOptions: { throw: true }
                })

                await Promise.all([
                    refetchInvitations?.(),
                    refetchOrganizations?.(),
                    onInvalidateInvitations?.()
                ])

                toast({
                    variant: "success",
                    message: localization.INVITATION_REJECTED
                })
            } catch (error) {
                toast({
                    variant: "error",
                    message: getLocalizedError({ error, localization })
                })
            }

            setIsLoading(false)
        },
        [authClient, toast, localization, refetchInvitations, refetchOrganizations, onInvalidateInvitations]
    )

    const pendingInvitations = invitations?.filter(
        (invitation) => invitation.status === "pending"
    )

    // The auth data hook stores Better Auth's `{ code, message }` error body,
    // which the shared AuthHook type models as a BetterFetchError.
    if (
        (invitationsError as { code?: string } | null | undefined)?.code ===
        EMAIL_VERIFICATION_REQUIRED_FOR_INVITATION
    ) {
        return (
            <p
                role="status"
                className={cn(
                    "text-muted-foreground text-sm",
                    classNames?.description
                )}
            >
                Verify your email to see invitations
            </p>
        )
    }

    if (!pendingInvitations?.length) return null

    return (
        <SettingsCard
            className={className}
            classNames={classNames}
            title={localization.PENDING_INVITATIONS}
            description={
                localization.PENDING_USER_INVITATIONS_DESCRIPTION ||
                localization.PENDING_INVITATIONS_DESCRIPTION
            }
            {...props}
        >
            <CardContent className={cn("grid gap-4", classNames?.content)}>
                {pendingInvitations.map((invitation) => (
                    <UserInvitationRow
                        key={invitation.id}
                        classNames={classNames}
                        invitationId={invitation.id}
                        isLoading={isLoading}
                        handleAccept={() => handleAccept(invitation.id)}
                        handleReject={() => handleReject(invitation.id)}
                    />
                ))}
            </CardContent>
        </SettingsCard>
    )
}

function UserInvitationRow({
    classNames,
    invitationId,
    handleAccept,
    handleReject,
    isLoading
}: {
    classNames?: SettingsCardProps["classNames"]
    invitationId: string
    isLoading: boolean
    handleAccept: () => Promise<void>
    handleReject: () => Promise<void>
}) {
    const {
        hooks: { useInvitation },
        localization: contextLocalization
    } = useContext(AuthUIContext)

    const localization = contextLocalization

    const { data: invitation } = useInvitation({
        query: {
            id: invitationId
        }
    })

    if (!invitation) return null

    return (
        <Card className={cn("flex-row items-center p-4", classNames?.cell)}>
            <div className="flex flex-1 items-center gap-2">
                <Avatar className="size-8">
                    <AvatarImage
                        src={invitation.organizationLogo || undefined}
                        alt={invitation.organizationName}
                    />
                    <AvatarFallback>
                        <Building className="size-[50%]" />
                    </AvatarFallback>
                </Avatar>

                <div className="grid flex-1 text-left leading-tight">
                    <span className="truncate text-sm font-semibold">
                        {invitation.organizationName}
                    </span>

                    <span className="text-muted-foreground truncate text-xs">
                        {localization.EXPIRES}{" "}
                        {invitation.expiresAt.toLocaleDateString()}
                    </span>
                </div>
            </div>

            <span className="text-muted-foreground truncate text-sm capitalize">
                {invitation.role}
            </span>

            <div className="ml-2 flex items-center">
                <Button
                    isLoading={isLoading}
                    title="Accept Invitation"
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                        void handleAccept()
                    }}
                >
                    <Check />
                </Button>
                <Button
                    isLoading={isLoading}
                    title="Reject Invitation"
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                        void handleReject()
                    }}
                >
                    <X className="text-destructive focus:text-destructive" />
                </Button>
            </div>
        </Card>
    )
}
